<template>
    <v-container class="pa-5" fluid>
        <ConfirmDialog
            :is-open="confirmDeleteNoteDialog"
            :data="currentNote"
            :action-loading="actionLoading.deleteNote"
            @on-confirm="deleteNote"
            @on-abort="closeConfirmDeleteNoteDialog"
        >
            <template #title>
                Usunięcie notki
            </template>
            <template #content>
                Czy na pewno chcesz usunąć notkę?
            </template>
            <template #confirmButton>
                Potwierdź
            </template>
        </ConfirmDialog>

        <page-title>Lista notek dla stanowisk produkcyjnych</page-title>

        <v-row align="center">
            <v-col cols="12" sm="6" md="4" lg="4" class="d-flex text-center">
                <v-btn
                    :loading="actionLoading.noteLoading"
                    color="secondary"
                    medium
                    data-sentry-label="Dodaj notkę"
                    @click="createNote"
                >
                    <v-icon left>
                        mdi-plus
                    </v-icon>
                    Dodaj notkę
                </v-btn>
                <v-switch
                    v-if="isAdmin"
                    v-model="unreadOnly"
                    inset
                    label="Tylko nieprzeczytane"
                    class="ml-8 mt-1"
                    @change="updateSort"
                />
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-data-table
                        :headers="headers"
                        :items="productionSiteNotes"
                        :loading="notesLoading"
                        :footer-props="footerProps"
                        :server-items-length="pagination.itemsLength"
                        :page.sync="pagination.page"
                        :mobile-breakpoint="0"
                        name="notesData"
                        @pagination="updatePagination"
                        @dblclick:row=" (event, payload) => openNote(payload.item)"
                    >
                        <template #item.title="{item}">
                            <span :class="{'font-weight-bold': !item.read}">{{ item.title }}</span>
                        </template>
                        <template #item.createdBy="{item}">
                            <template v-if="item.createdBy && item.createdBy.firstName && item.createdBy.lastName">
                                <span :class="{'font-weight-bold': !item.read}">{{ item.createdBy.firstName }} {{ item.createdBy.lastName }}</span>
                            </template>
                            <template v-else>
                                -
                            </template>
                        </template>
                        <template #item.createdAt="{item}">
                            <span :class="{'font-weight-bold': !item.read}">{{ formatDate(item.createdAt) }}</span>
                        </template>
                        <template #item.expireDateTime="{item}">
                            <span :class="{'font-weight-bold': !item.read}">{{ formatDate(item.expireDateTime) }}</span>
                        </template>
                        <template #item.originProductionSite="{item}">
                            <template v-if="item.originProductionSite">
                                <span :class="{'font-weight-bold': !item.read}">{{ item.originProductionSite.name }}</span>
                            </template>
                            <template v-else>
                                -
                            </template>
                        </template>
                        <template #item.approved="{item}">
                            <v-icon
                                v-if="item.approved"
                                small
                                color="success"
                            >
                                mdi-check
                            </v-icon>
                            <v-icon v-else small color="error">
                                mdi-close
                            </v-icon>
                        </template>
                        <template #item.adminAccepted="{item}">
                            <v-icon v-if="item.adminAccepted" small color="success">
                                mdi-check
                            </v-icon>
                            <v-icon v-else small color="error">
                                mdi-close
                            </v-icon>
                        </template>
                        <template #item.actions="{item}">
                            <div class="d-inline-flex">
                                <v-tooltip v-if="isAdmin" :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            :disabled="item.adminAccepted"
                                            icon
                                            :loading="actionLoading.acceptNote[item.id]"
                                            data-sentry-label="Zatwierdź"
                                            v-on="on"
                                            @click="acceptNote(item)"
                                        >
                                            <v-icon v-if="!item.adminAccepted" color="green">
                                                mdi-check-circle
                                            </v-icon>
                                            <v-icon v-else color="green">
                                                mdi-check-circle-outline
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Zatwierdź</span>
                                </v-tooltip>
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            data-sentry-label="Otwórz"
                                            v-on="on"
                                            @click="openNote(item)"
                                        >
                                            <v-icon color="blue">
                                                mdi-ballot
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Otwórz</span>
                                </v-tooltip>
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template v-if="canDelete" #activator="{on}">
                                        <v-btn
                                            icon
                                            data-sentry-label="Usuń"
                                            v-on="on"
                                            @click="openConfirmDeleteNoteDialog(item)"
                                        >
                                            <v-icon color="pink">
                                                mdi-delete
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Usuń</span>
                                </v-tooltip>
                            </div>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import router from '@/router'

import PageTitle from '@/components/common/PageTitle'
import ConfirmDialog from '@/components/common/ConfirmDialog'

import filters from '@/mixins/dataTableFilters'
import pagination from '@/mixins/dataTablePagination'
import tooltipDelay from '@/mixins/tooltip'
import dateFormatter from '@/mixins/dateFormatter'
import dataTableClassSetter from '@/mixins/dataTableClassSetter'

import { errorHandler } from '@/utils/errorHandler'
import { EventBus } from '@/utils/eventBus'
import { hasUserPermission } from '@/utils/permissionsHandler'

export default {
    name: 'NotesProductionSiteNotes',
    components: {
        PageTitle, ConfirmDialog
    },
    mixins: [
        tooltipDelay,
        filters,
        pagination,
        dateFormatter,
        dataTableClassSetter
    ],
    beforeRouteLeave(to, from, next) {
        if (!this.filtersCleared) {
            this.$store.dispatch('notes/clearProductionSiteNotesFilters')
        }
        this.resetFiltersCleared()
        next()
    },
    data() {
        return {
            currentNote: {},
            unreadOnly: true,
            confirmDeleteNoteDialog: false,
            actionLoading: {
                deleteNote: false,
                noteLoading: false,
                acceptNote: {}
            },
            headers: [
                {
                    text: 'Tytuł',
                    value: 'title',
                    sortable: false,
                    width: 350
                },
                {
                    text: 'Autor',
                    value: 'createdBy',
                    sortable: false,
                    align: 'left',
                },
                {
                    text: 'Data utworzenia',
                    value: 'createdAt',
                    sortable: false,
                    align: 'left',
                },
                {
                    text: 'Data wygaśnięcia',
                    value: 'expireDateTime',
                    sortable: false,
                    align: 'left',
                },
                {
                    text: 'Stanowisko',
                    value: 'originProductionSite',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Czy udostępniona',
                    value: 'approved',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Czy zatwierdzona',
                    value: 'adminAccepted',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: 80
                }
            ]
        }
    },
    computed: {
        isAdmin() {
            return hasUserPermission('production_sites_notes_admin')
        },
        canDelete() {
            return hasUserPermission('production_sites_notes_admin_delete')
        },
        canUpdate() {
            return hasUserPermission(['production_sites_notes_admin_update', 'production_sites_note_leader_update'])
        },
        userId() {
            return this.$store.state.user.userId
        },
        productionSiteNotes() {
            return this.$store.state.notes.productionSiteNotes.productionSiteNotes
        },
        unreadProductionSiteNotes() {
            return this.$store.state.notes.productionSiteNotes.unreadProductionSiteNotes
        },
        notesLoading() {
            return this.$store.state.notes.productionSiteNotes.productionSiteNotesLoading
        },
        savedFilters() {
            return this.$store.state.notes.productionSiteNotes.productionSiteNotesFilters
        },
    },
    beforeMount() {
        if (!this.savedPagination.pagination) {
            this.resetPagination()
        }
    },
    mounted() {
        if (!this.unreadProductionSiteNotes || !this.isAdmin) {
            this.unreadOnly = false
        }
        this.getNotes()
    },
    methods: {
        filterQuery() {
            let filters = Object.assign({}, this.filters)
            let query = ''

            if (this.unreadOnly) {
                filters = Object.assign({'unreadBy_id': this.userId}, this.filters)
            }

            for (let item in filters) {
                if (filters[item] != null && filters[item] != '') {
                    query = query.concat('&', `${item}=${filters[item]}`)
                }
            }

            return query
        },
        getNotes() {
            this.actionLoading.notesLoading = true
            this.$store.dispatch('notes/getProductionSiteNotes', {
                paginate: this.paginate(),
                query: this.filterQuery()
            })
                .then(response => {
                    this.handlePaginationResponse(response)
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.notesLoading = false
                })
        },
        createNote() {
            this.actionLoading.noteLoading = true
            router.push({ name: 'notesProductionSiteNoteCreate'})
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.noteLoading = false
                })
        },
        deleteNote(note) {
            this.actionLoading.deleteNote = true
            this.$store.dispatch('notes/deleteProductionSiteNote', note.id)
                .then(() => {
                    this.getNotes()
                    this.closeConfirmDeleteNoteDialog()
                    this.decrementProductionSiteNoteCount()
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.deleteNote = false
                })
        },
        openConfirmDeleteNoteDialog(note) {
            this.confirmDeleteNoteDialog = true
            this.currentNote = note
        },
        closeConfirmDeleteNoteDialog() {
            this.confirmDeleteNoteDialog = false
            this.currentNote = {}
        },
        updatePagination(pagination) {
            this.pagination = pagination
            this.evaluateSavedPagination(this.$options.name)

            if (pagination.pageCount !== 0 
                && !this.paginationSettings.paginationPending 
                && !this.savedPagination.pagination
            ) {
                this.getNotes()
            }
            
            this.clearPaginationPending()
        },
        openNote(note) {
            this.savePagination(this.$options.name)
            this.saveFiltersCleared()
            this.setSelectedListItemId(note.id)
            router.push({ name: 'notesProductionSiteNoteEdit', params: { id: note.id } })
                .then(() => {
                    if (!note.read) {
                        this.decrementProductionSiteNoteCount()
                    }
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        acceptNote(note) {
            this.actionLoading.acceptNote[note.id] = true
            this.$store.dispatch('notes/updateProductionSiteNote', {...note, ...{adminAccepted: true}})
                .then(() => {
                    this.$store.dispatch('showNotification', {
                        type: 'success',
                        message: 'Notka została zatwierdzona.'
                    })
                    note.adminAccepted = true
                    this.actionLoading.acceptNote[note.id] = false
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        updateSort() {
            this.resetPagination()
            this.getNotes()
        },
        decrementProductionSiteNoteCount() {
            let unread = this.$store.state.notes.productionSiteNotes.unreadProductionSiteNotes

            EventBus.$emit('updatedProductionSiteNoteCount', unread - 1)
        }
    }
}

</script>